import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import AuthLayout from '../../components/AuthLayout';
import GoogleSignInButton from '../../components/GoogleSignInButton';
import { expertAuthApi, getErrorMessage } from '../../lib/http';

// Validation schema
const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(10, 'Password must be at least 10 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const ExpertLogin: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [googleAccessToken, setGoogleAccessToken] = useState<string>('');
  const [suggestedName, setSuggestedName] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setError('');
    setIsLoading(true);

    try {
      const response = await expertAuthApi.signin({
        email: data.email,
        password: data.password,
      });

      // Store session info and expert data
      sessionStorage.setItem('sessionRole', 'expert');
      sessionStorage.setItem('expert', JSON.stringify(response.data.expert));

      // Redirect to expert dashboard
      navigate('/app/dashboard');
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (accessToken: string) => {
    setError('');
    setIsLoading(true);

    try {
      const response = await expertAuthApi.googleAuth({ access_token: accessToken });
      sessionStorage.setItem('sessionRole', 'expert');
      sessionStorage.setItem('expert', JSON.stringify(response.data.expert));

      // Check if phone verification is needed
      if (response.data.requires_phone) {
        navigate('/expert/complete-profile');
      } else {
        navigate('/app/dashboard');
      }
    } catch (err: any) {
      // Check if we need to collect display_name for new expert
      if (err.response?.data?.requires_profile) {
        setGoogleAccessToken(accessToken);
        setSuggestedName(err.response.data.suggested_name || '');
        setDisplayName(err.response.data.suggested_name || '');
        setShowProfileForm(true);
        setIsLoading(false);
        return;
      }
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const response = await expertAuthApi.googleAuth({
        access_token: googleAccessToken,
        display_name: displayName.trim(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      sessionStorage.setItem('sessionRole', 'expert');
      sessionStorage.setItem('expert', JSON.stringify(response.data.expert));

      // Check if phone verification is needed
      if (response.data.requires_phone) {
        navigate('/expert/complete-profile');
      } else {
        navigate('/app/dashboard');
      }
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google sign in failed. Please try again.');
  };

  // Profile form for new Google users
  if (showProfileForm) {
    return (
      <AuthLayout role="expert" title="Complete Your Profile">
        <form onSubmit={handleProfileSubmit} className="space-y-5">
          {error && (
            <div
              role="alert"
              className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm"
              aria-live="polite"
            >
              {error}
            </div>
          )}

          <div className="bg-pink-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-pink-800">
              Just one more step! Enter your display name to complete your expert profile.
            </p>
          </div>

          <div>
            <label
              htmlFor="displayName"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Display Name
            </label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={suggestedName || 'Your business name'}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
            />
            <p className="mt-1 text-xs text-gray-500">This is how clients will see you</p>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-pink-600 hover:bg-pink-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Creating profile...' : 'Complete Setup'}
          </button>

          <button
            type="button"
            onClick={() => {
              setShowProfileForm(false);
              setGoogleAccessToken('');
            }}
            className="w-full text-gray-600 hover:text-gray-800 text-sm py-2"
          >
            Cancel
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout role="expert" title="" subtitle="">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Error Alert */}
        {error && (
          <div
            role="alert"
            className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm"
            aria-live="polite"
          >
            {error}
          </div>
        )}

        {/* Email Field */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Email
          </label>
          <input
            {...register('email')}
            id="email"
            type="email"
            autoComplete="email"
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors ${
              errors.email ? 'border-red-300' : 'border-gray-300'
            }`}
            aria-invalid={errors.email ? 'true' : 'false'}
            aria-describedby={errors.email ? 'email-error' : undefined}
          />
          {errors.email && (
            <p id="email-error" className="mt-1 text-sm text-red-600">
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password Field */}
        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Password
          </label>
          <input
            {...register('password')}
            id="password"
            type="password"
            autoComplete="current-password"
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors ${
              errors.password ? 'border-red-300' : 'border-gray-300'
            }`}
            aria-invalid={errors.password ? 'true' : 'false'}
            aria-describedby={errors.password ? 'password-error' : undefined}
          />
          {errors.password && (
            <p id="password-error" className="mt-1 text-sm text-red-600">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-pink-600 hover:bg-pink-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-pink-200"
        >
          {isLoading ? 'Logging in...' : 'Log in'}
        </button>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">or</span>
          </div>
        </div>

        {/* Google Sign In */}
        <GoogleSignInButton
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          disabled={isLoading}
        />

        {/* Links */}
        <div className="flex items-center justify-between text-sm">
          <Link
            to="/expert/forgot"
            className="text-pink-600 hover:underline"
          >
            Forgot password?
          </Link>

          <Link
            to="/expert/signup"
            className="text-pink-600 hover:underline font-medium"
          >
            Create account
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
};

export default ExpertLogin;
